/**
 * The snapshot the asset republish compares against (CONTENT_MODEL_SPEC §3.6,
 * SCENE_ROOT_SPEC §2.2, D8).
 *
 * One function rather than two call sites, because the two call sites are what
 * went wrong. The editor takes a snapshot when an asset is opened and compares
 * it when Publish is pressed; both used `getExportData()`, which deliberately
 * **excludes the root** — while the document the publish writes includes it.
 *
 * So a behavior added to the asset's own root — a Rotate on the whole asset,
 * its Frame, its Properties — changed nothing the comparison could see. Publish
 * answered "Already up to date", marked the session clean (the dirty dot went
 * green), sent no PATCH, and the edit was gone on the next open. Silently: the
 * one shape of bug a save path must never have.
 *
 * `getFullExportData()` is the whole document — under D8 one root record and
 * everything below it, with file-local parent paths — and it is what the CMS
 * scene path has always snapshotted. The root it carries is the **document
 * root**, an ordinary node; the engine mount point is not in it at all.
 *
 * The baseline is taken AFTER `adoptDocumentRoot` (see `document-root.ts`), so
 * a freshly opened document is never dirty for having been adopted.
 */

import { snapshotExportData } from 'trace-viewer/editor';
import type { SceneObjectData } from 'trace-viewer';
import type { TraceScene } from 'trace-viewer/scene';
import { editorExclusion } from '../editor-content';

/**
 * Everything an asset publish would write — content objects AND the document
 * root's own behaviors. The one reader of `getFullExportData` on this path,
 * so the two sides of the comparison cannot drift apart again.
 */
export function assetExportData(traceScene: TraceScene | null | undefined): SceneObjectData[] {
    return traceScene?.getFullExportData(editorExclusion(traceScene)) ?? [];
}

/**
 * That data, serialized — what the editor records when an asset is opened and
 * what `resolvePublishDecision` re-derives when Publish is pressed.
 */
export function assetContentSnapshot(traceScene: TraceScene | null | undefined): string | null {
    return traceScene ? snapshotExportData(assetExportData(traceScene)) : null;
}
